import {
  CoverCropper,
  type CoverCropperLabels,
} from "@/components/cover-cropper"
import { COVER_ASPECT_CLASS, COVER_ASPECT_RATIO } from "@/lib/mission-cover"

/**
 * OKS-697 — Mission-cover crop step.
 *
 * Opened by `MissionCoverImageField` when the admin picks or replaces a
 * cover. Presents a `react-easy-crop` viewport LOCKED to the mission
 * cover ratio (`COVER_ASPECT_RATIO`, 3:2) so the framed area is exactly
 * what every display surface renders — no server-side center-crop, no
 * arbitrary band. On *Appliquer* the crop rectangle is rasterised to a
 * `Blob` (via the mockable `getCroppedBlob` canvas seam) and handed back
 * through `onApply`; the field stages it into the hidden multipart file
 * input and the parent Inertia `<Form>` submits it unchanged.
 *
 * OKS-768 — this is now a thin wrapper over the shared `CoverCropper`,
 * binding the mission ratio (`COVER_ASPECT_RATIO`/`COVER_ASPECT_CLASS`),
 * the `mission-cover-crop` testId prefix, and the mission i18n keys. The
 * rendered DOM, testids, and copy are byte-identical to the pre-OKS-768
 * component, so OKS-697's cropper/field Vitest and
 * `mission-cover-crop-oks697.spec.ts` are unaffected.
 *
 * Crew form rules hold: this is a crop widget, NOT a form — no
 * react-hook-form / zod. The only form on the page is the parent Inertia
 * `<Form>`; validation errors still render from its `errors` render-prop.
 */

const MISSION_COVER_CROP_LABELS: CoverCropperLabels = {
  title: "missions.media.cover_crop_title",
  description: "missions.media.cover_crop_description",
  zoom: "missions.media.cover_crop_zoom",
  cancel: "missions.media.cover_crop_cancel",
  apply: "missions.media.cover_crop_apply",
  decodeError: "missions.media.cover_crop_decode_error",
}

export interface MissionCoverCropperProps {
  /** Open state — controlled by the parent field. */
  open: boolean
  /** Object URL of the picked source image. Null when nothing staged. */
  imageSrc: string | null
  /** Source MIME type; passed through to the canvas export so PNG/WEBP
   *  sources keep their type (defaults to JPEG inside the seam). */
  mimeType?: string
  /** Called with the cropped Blob on *Appliquer*. */
  onApply: (blob: Blob) => void
  /** Called on *Annuler* / ESC / overlay dismiss — no file staged. */
  onCancel: () => void
  testId?: string
}

export function MissionCoverCropper({
  open,
  imageSrc,
  mimeType,
  onApply,
  onCancel,
  testId = "mission-cover-crop",
}: MissionCoverCropperProps) {
  return (
    <CoverCropper
      open={open}
      imageSrc={imageSrc}
      mimeType={mimeType}
      aspectRatio={COVER_ASPECT_RATIO}
      aspectClass={COVER_ASPECT_CLASS}
      labels={MISSION_COVER_CROP_LABELS}
      onApply={onApply}
      onCancel={onCancel}
      testId={testId}
    />
  )
}

export default MissionCoverCropper
